import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Box, Form, ResponsiveContext, Tip } from 'grommet';
import { observer } from 'mobx-react-lite';
import { ApiError, ClientService } from '/src/api';
import { BreadcrumbNav, EntityIcon, FormCard, FormCardSection, FormPage, Input, Line, Text } from '/src/components';
import { config } from '/src/config';
import { useEquipmentStore, useEquipmentUsageStore, useGlobalStore, useUserStore } from '/src/context';
import { Equipment, EquipmentUsage, Model, ModelName, isFciCategory } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { EquipmentUsageDataForm, ReportingPeriodType } from '/src/lib/types';
import { PageNotFound } from '/src/pages';
import {
  formatNumber,
  getFormErrors,
  getIsMobile,
  getModelLabel,
  getPageTitle,
  getPeriodFromDateString,
} from '/src/utils';

export const EquipmentUsageDetailsPage: React.FC<EquipmentUsageDetailsProps> = observer(() => {
  /** Context **/
  const globalStore = useGlobalStore();
  const userStore = useUserStore();
  const equipmentStore = useEquipmentStore();
  const equipmentUsageStore = useEquipmentUsageStore();
  const screenSize = useContext(ResponsiveContext);
  const params = useParams();
  const { t } = useTranslation();

  /** State **/
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [clientName, setClientName] = useState('');
  const [equipmentUsage, setEquipmentUsage] = useState<EquipmentUsage>();
  const [equipment, setEquipment] = useState<Equipment>();

  const [formValues, setFormValues] = useState<EquipmentUsageDataForm>({
    fuel_pathway_id: undefined,
    is_book_and_claim_applied: false,
    battery_capacity_rating_ah: '',
    voltage: '',
    is_paused: false,
    total_kwh: null,
    charge_cycles_per_shift: '',
    shifts_per_day: '',
    work_days_per_quarter: '',
    percent_charger_efficiency_rating: '',
    percent_charge_return_factor: '',
    percent_depth_of_discharge: '',
    percent_uptime: '95',
    max_site_deliverability_kw: '',
  });
  const [initialFormValues, setInitialFormValues] = useState<EquipmentUsageDataForm>();
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  /** Computed **/
  const isMobile = getIsMobile(screenSize);
  const clientId = parseInt(params.client_id ?? '');
  const equipmentId = parseInt(params.equipment_id ?? '');
  const equipmentUsageId = parseInt(params.equipment_usage_id ?? '');
  const equipmentLabel = equipment ? getModelLabel(equipment as Model, ModelName.Equipment) : undefined;
  const isInvalidParams = Number.isNaN(clientId) || Number.isNaN(equipmentId) || Number.isNaN(equipmentUsageId);
  const isCanada = equipmentUsage?.reporting_period_type_id === ReportingPeriodType.Yearly;
  const isMetered = equipment?.is_metered;
  const showFciFields = isFciCategory(equipment?.equipment_type.equipment_category?.id);

  const reportingQuarter = equipmentUsage
    ? getPeriodFromDateString(equipmentUsage.start_reporting_quarter, isCanada)
    : undefined;

  const canEditQuarter =
    reportingQuarter && globalStore.currentQuarter && reportingQuarter === globalStore.currentQuarter;

  const hasErrors = !!Object.values(formErrors).filter((e) => !!e).length;

  const hasChanges =
    !!initialFormValues &&
    Object.keys(formValues).some((key) => String(formValues[key] ?? '') !== String(initialFormValues[key] ?? ''));

  /** Methods **/
  const updateFormValue = (key: string, value: number | null) => {
    if (value !== null) {
      if (key === 'charge_cycles_per_shift' && value > 9.99) {
        setFormErrors({ ...formErrors, charge_cycles_per_shift: 'Value cannot exceed 9.99' });
      } else if (key === 'shifts_per_day' && value > 9.99) {
        setFormErrors({ ...formErrors, shifts_per_day: 'Value cannot exceed 9.99' });
      } else if (key === 'work_days_per_quarter' && (value as number) > 92) {
        setFormErrors({ ...formErrors, work_days_per_quarter: 'Value cannot exceed 92 days' });
      } else if (key === 'charge_cycles_per_shift' && value < 0) {
        setFormErrors({ ...formErrors, charge_cycles_per_shift: 'Charge Cycles / Shift must be 0 or greater' });
      } else if (key === 'work_days_per_quarter' && value < 0) {
        setFormErrors({ ...formErrors, work_days_per_quarter: 'Work Days / Quarter must be 0 or greater' });
      } else if (key === 'shifts_per_day' && value < 0) {
        setFormErrors({ ...formErrors, shifts_per_day: 'Shifts / Day must be 0 or greater' });
      } else if (key === 'percent_charger_efficiency_rating' && value < 0) {
        setFormErrors({
          ...formErrors,
          percent_charger_efficiency_rating: 'Charger Efficiency Rating must be 0 or greater',
        });
      } else if (key === 'percent_charge_return_factor' && value < 0) {
        setFormErrors({ ...formErrors, percent_charge_return_factor: 'Charge Return Factor must be 0 or greater' });
      } else if (key === 'percent_depth_of_discharge' && value < 0) {
        setFormErrors({ ...formErrors, percent_depth_of_discharge: 'Depth of Discharge must be 0 or greater' });
      } else if (key === 'total_kwh' && value < 0) {
        setFormErrors({ ...formErrors, total_kwh: 'Total kWh must be 0 or greater' });
      }
    }

    setFormValues({
      ...formValues,
      [key]: value,
    });
    if (formErrors[key]) {
      setFormErrors({
        ...formErrors,
        [key]: '',
      });
    }
  };

  const fetchEquipmentUsage = async () => {
    if (!equipmentUsageId) return;
    const equipmentUsage = await equipmentUsageStore.getEquipmentUsage({ id: equipmentUsageId });
    setEquipmentUsage(equipmentUsage);
    const values: EquipmentUsageDataForm = {
      fuel_pathway_id: equipmentUsage?.fuel_pathway_id,
      is_book_and_claim_applied: equipmentUsage?.is_book_and_claim_applied ?? false,
      battery_capacity_rating_ah: equipmentUsage?.battery_capacity_rating_ah?.toString() ?? '',
      voltage: equipmentUsage?.voltage?.toString() ?? '',
      is_paused: equipmentUsage?.is_paused ?? false,
      total_kwh: equipmentUsage?.total_kwh ?? null,
      charge_cycles_per_shift: equipmentUsage?.charge_cycles_per_shift ?? '',
      shifts_per_day: equipmentUsage?.shifts_per_day ?? '',
      work_days_per_quarter: equipmentUsage?.work_days_per_quarter ?? '',
      percent_charger_efficiency_rating: equipmentUsage?.percent_charger_efficiency_rating?.toString() ?? '',
      percent_charge_return_factor: equipmentUsage?.percent_charge_return_factor?.toString() ?? '',
      percent_depth_of_discharge: equipmentUsage?.percent_depth_of_discharge?.toString() ?? '',
      percent_uptime: equipmentUsage?.percent_uptime ?? '95',
      max_site_deliverability_kw: equipmentUsage?.max_site_deliverability_kw ?? null,
    };
    setFormValues(values);
    setInitialFormValues(values);
  };

  const fetchPageData = async () => {
    if (isLoading || !clientId || !equipmentId || !equipmentUsageId) return;

    try {
      setIsLoading(true);

      const clientName =
        userStore.isClientUser || userStore.isFacilityUser
          ? userStore.user?.clients.find((client) => client.id === clientId)?.name
          : (await ClientService.get({ id: clientId }))?.name;
      setClientName(clientName ?? '');

      const equipment = await equipmentStore.fetchEquipment(equipmentId);
      setEquipment(equipment);

      await fetchEquipmentUsage();
    } catch (err) {
      globalStore.handleApiError(err as ApiError, toastMessages.fetchEquipmentUsage.error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateMeteredUsage = async (): Promise<boolean> => {
    if (!equipment || !equipmentUsage || !canEditQuarter) return false;
    const errors = getFormErrors(
      {
        total_kwh: { label: 'Total kWh', required: true },
        percent_uptime: { label: 'Uptime', required: true },
        max_site_deliverability_kw: { label: 'Max Site Deliverability (kW)', required: false },
      },
      formValues
    );
    if (errors) {
      setFormErrors(errors);
      return false;
    }

    await equipmentUsageStore.updateMeteredEquipmentUsage({
      equipment_usage_id: equipmentUsage.id,
      fuel_pathway_id: userStore.isInternalUser ? (formValues.fuel_pathway_id ?? null) : null,
      is_book_and_claim_applied: userStore.isInternalUser ? (formValues.is_book_and_claim_applied ?? null) : null,
      total_kwh: formValues.total_kwh ? parseFloat(parseFloat(formValues.total_kwh as string).toFixed(2)) : null,
      percent_uptime: formValues.percent_uptime ? parseInt(formValues.percent_uptime) : null,
      max_site_deliverability_kw: formValues.max_site_deliverability_kw
        ? parseInt(formValues.max_site_deliverability_kw as string)
        : null,
    });

    return true;
  };

  const updateUnmeteredUsage = async (): Promise<boolean> => {
    if (!equipment || !equipmentUsage || !canEditQuarter) return false;
    const errors = getFormErrors(
      {
        charge_cycles_per_shift: { label: 'Charge Cycles / Shift', required: true, max: 9.99, nonZero: true },
        shifts_per_day: { label: 'Shifts / Day', required: true, max: 9.99, nonZero: true },
        work_days_per_quarter: { label: 'Work Days / Quarter', required: true, max: 92, nonZero: true },
        percent_charger_efficiency_rating: {
          label: 'Charger Efficiency Rating',
          required: true,
        },
        percent_charge_return_factor: { label: 'Charge Return Factor', required: true },
        percent_depth_of_discharge: { label: 'Depth of Discharge', required: true },
        percent_uptime: { label: 'Uptime', required: true },
        max_site_deliverability_kw: { label: 'Max Site Deliverability (kW)', required: false },
      },
      formValues
    );
    if (errors) {
      setFormErrors(errors);
      return false;
    }

    await equipmentUsageStore.updateUnmeteredEquipmentUsage({
      equipment_usage_id: equipmentUsage.id,
      fuel_pathway_id: userStore.isInternalUser ? (formValues.fuel_pathway_id ?? null) : null,
      is_book_and_claim_applied: userStore.isInternalUser ? (formValues.is_book_and_claim_applied ?? null) : null,
      battery_capacity_rating_ah: equipmentUsage?.battery_capacity_rating_ah ?? NaN,
      voltage: equipmentUsage?.voltage ?? NaN,
      is_paused: equipmentUsage?.is_paused ?? false,
      charge_cycles_per_shift: parseFloat(parseFloat(formValues.charge_cycles_per_shift as string).toFixed(2)),
      shifts_per_day: parseFloat(parseFloat(formValues.shifts_per_day as string).toFixed(2)),
      work_days_per_quarter: parseFloat(parseFloat(formValues.work_days_per_quarter as string).toFixed(2)),
      percent_charger_efficiency_rating: parseInt(formValues.percent_charger_efficiency_rating as string),
      percent_charge_return_factor: parseInt(formValues.percent_charge_return_factor as string),
      percent_depth_of_discharge: parseInt(formValues.percent_depth_of_discharge as string),
      percent_uptime: parseInt(formValues.percent_uptime as string),
      max_site_deliverability_kw: formValues.max_site_deliverability_kw
        ? parseInt(formValues.max_site_deliverability_kw as string)
        : null,
    });

    return true;
  };

  const updateEquipmentUsage = async () => {
    if (isUpdating || !equipment || !hasChanges) return;

    try {
      setIsUpdating(true);

      const saved = isMetered ? await updateMeteredUsage() : await updateUnmeteredUsage();
      if (!saved) return;

      toast.success(toastMessages.updateEquipmentUsage.success);

      await fetchEquipmentUsage();
    } catch (err) {
      globalStore.handleApiError(err as ApiError, toastMessages.updateEquipmentUsage.error);
    } finally {
      setIsUpdating(false);
    }
  };

  /** Effects **/
  useEffect(() => {
    document.title = getPageTitle(
      config.client.basePageTitle,
      equipment?.name ?? `${equipment?.manufacturer}: ${equipment?.serial_number}`,
      'Usage'
    );
  }, [equipment]);

  useEffect(() => {
    if (clientId && equipmentId && equipmentUsageId) {
      fetchPageData();
    }
  }, [clientId, equipmentId, equipmentUsageId]);

  /** Render **/
  return isInvalidParams ? (
    <PageNotFound />
  ) : (
    <FormPage
      title={equipmentLabel}
      titleLinkUrl={`/clients/${clientId}/equipment/${equipmentId}`}
      titleLinkIconSize="18px"
      breadcrumbNav={
        <BreadcrumbNav
          previousPages={[
            { name: 'Clients', link: !userStore.isClientUser && !userStore.isFacilityUser ? '/clients' : undefined },
            { name: clientName, link: !userStore.isFacilityUser ? `/clients/${clientId}` : undefined },
            {
              name: 'Equipment',
              link: equipment?.facility_id
                ? `/clients/${clientId}/facilities/${equipment?.facility_id}/equipment`
                : undefined,
            },
            {
              name: equipmentLabel ?? '',
              link: `/clients/${clientId}/equipment/${equipmentId ?? ''}`,
            },
            { name: 'Usages ' },
          ]}
          currentPageName={getPeriodFromDateString(equipmentUsage?.start_reporting_quarter ?? '', isCanada)}
        />
      }
      isLoading={isLoading}
      isDetailPage
    >
      <Form>
        <FormCard
          title={`${reportingQuarter} ${t('equipment:usage')}`}
          icon={<EntityIcon entityName={'EquipmentUsage'} size="24px" color="#EAAD47" />}
          onSubmit={updateEquipmentUsage}
          isLoading={isLoading || isUpdating}
          hideRequiredText={!canEditQuarter}
          hideSaveButton={!canEditQuarter}
          saveButtonLabel={t('common:saveChanges')}
          saveButtonDisabled={hasErrors || !hasChanges}
        >
          {equipment && equipmentUsage && (
            <Box direction={isMobile ? 'column' : 'row'} margin={{ bottom: '1rem' }}>
              <Box width="60%" gap="1rem">
                <FormCardSection title={t('equipment:equipmentDetails')}>
                  <Box direction="row">
                    <Box width="25%" gap="xsmall">
                      <Text color="accent-1" size="small" toUpperCase>
                        {t('equipment:category')}
                      </Text>
                      <Text fontFamily="Lato, sans-serif">{equipment.equipment_type.equipment_category?.name}</Text>
                    </Box>
                    <Box width="50%" gap="xsmall">
                      <Text color="accent-1" size="small" toUpperCase>
                        {t('equipment:type')}
                      </Text>
                      <Text fontFamily="Lato, sans-serif">{equipment.equipment_type.name}</Text>
                    </Box>
                    <Box width="25%" gap="xsmall">
                      <Text color="accent-1" size="small" toUpperCase>
                        {t('equipment:metered')}
                      </Text>
                      <Text fontFamily="Lato, sans-serif">{isMetered ? t('common:yes') : t('common:no')}</Text>
                    </Box>
                  </Box>
                  <Line margin="0.5rem" />
                  <Box direction="row">
                    <Box width="25%" gap="xsmall">
                      <Text color="accent-1" size="small" toUpperCase>
                        {t('equipment:manufacturer')}
                      </Text>
                      <Text fontFamily="Lato, sans-serif">{equipment.manufacturer}</Text>
                    </Box>
                    <Box width="25%" gap="xsmall">
                      <Text color="accent-1" size="small" toUpperCase>
                        {t('equipment:modelYear')}
                      </Text>
                      <Text fontFamily="Lato, sans-serif">{equipment.model_year}</Text>
                    </Box>
                    <Box width="25%" gap="xsmall">
                      <Text color="accent-1" size="small" toUpperCase>
                        {t('equipment:serialNumber')}
                      </Text>
                      <Text fontFamily="Lato, sans-serif">
                        <Tip content={equipment.serial_number}>
                          {equipment.serial_number.length >= 21
                            ? `${equipment.serial_number.slice(0, 4)}...${equipment.serial_number.slice(-4)}`
                            : equipment.serial_number}
                        </Tip>
                      </Text>
                    </Box>
                    <Box width="25%" gap="xsmall">
                      <Text color="accent-1" size="small" toUpperCase>
                        {t('equipment:unitNo')}
                      </Text>
                      <Text fontFamily="Lato, sans-serif">{equipment.unit_number}</Text>
                    </Box>
                  </Box>
                  <Box direction="row">
                    <Box width="25%" gap="xsmall">
                      <Text color="accent-1" size="small" toUpperCase>
                        {t('equipment:fuelPathwayCode')}
                      </Text>
                      <Text fontFamily="Lato, sans-serif">
                        {equipmentUsage.fuel_pathway_id == null
                          ? ''
                          : equipmentUsageStore.fuelPathwayOptions.find(
                              (option) => option.value === equipmentUsage.fuel_pathway_id
                            )?.label || equipmentUsage.fuel_pathway_id}
                      </Text>
                    </Box>
                    <Box width="25%" gap="xsmall">
                      <Text color="accent-1" size="small" toUpperCase>
                        {t('equipment:bookAndClaim')}
                      </Text>
                      <Text fontFamily="Lato, sans-serif">
                        {equipmentUsage.is_book_and_claim_applied === null ||
                        equipmentUsage.is_book_and_claim_applied === undefined
                          ? ''
                          : equipmentUsage.is_book_and_claim_applied
                            ? t('equipment:applied')
                            : t('equipment:notApplied')}
                      </Text>
                    </Box>
                    {!isMetered && (
                      <>
                        <Box width="25%" gap="xsmall">
                          <Text color="accent-1" size="small" toUpperCase>
                            {t('equipment:batteryCapacityAh')}
                          </Text>
                          <Text fontFamily="Lato, sans-serif">{equipmentUsage.battery_capacity_rating_ah}</Text>
                        </Box>
                        <Box width="25%" gap="xsmall">
                          <Text color="accent-1" size="small" toUpperCase>
                            {t('equipment:voltage')}
                          </Text>
                          <Text fontFamily="Lato, sans-serif">{equipmentUsage.voltage}</Text>
                        </Box>
                      </>
                    )}
                  </Box>
                </FormCardSection>
              </Box>
              <Line direction="vertical" />
              <Box width="40%" gap="1rem">
                {isMetered && (
                  <FormCardSection title={t('equipment:usageDetails')}>
                    <Box direction="column" gap="1rem">
                      {canEditQuarter ? (
                        <Box direction="column" gap="0.75rem">
                          <Box direction="row" gap="1rem" wrap={false}>
                            <Box basis="0" flex={{ grow: 1, shrink: 1 }}>
                              <Input
                                name="total_kwh"
                                label={t('common:totalKwh')}
                                value={formValues['total_kwh'] ? (formValues['total_kwh'] as string) : ''}
                                setValue={(value) => updateFormValue('total_kwh', value ? parseFloat(value) : null)}
                                error={formErrors['total_kwh']}
                                onSubmit={updateEquipmentUsage}
                                required
                                fill="horizontal"
                              />
                            </Box>
                          </Box>
                          {showFciFields && (
                            <Box direction="row" gap="1rem" wrap={false}>
                              <Box basis="0" flex={{ grow: 1, shrink: 1 }}>
                                <Input
                                  label={t('equipment:percentUptime')}
                                  componentType="percentage"
                                  value={formValues['percent_uptime'] ? (formValues['percent_uptime'] as string) : ''}
                                  setValue={(value) =>
                                    updateFormValue('percent_uptime', value ? parseInt(value) : null)
                                  }
                                  name="percent_uptime"
                                  error={formErrors['percent_uptime']}
                                  onSubmit={updateEquipmentUsage}
                                  required
                                  fill="horizontal"
                                />
                              </Box>
                              <Box basis="0" flex={{ grow: 1, shrink: 1 }}>
                                <Input
                                  label={t('equipment:maxSiteDeliverabilityKw')}
                                  value={
                                    formValues['max_site_deliverability_kw']
                                      ? (formValues['max_site_deliverability_kw'] as string)
                                      : ''
                                  }
                                  setValue={(value) =>
                                    updateFormValue('max_site_deliverability_kw', value ? parseInt(value) : null)
                                  }
                                  name="max_site_deliverability_kw"
                                  error={formErrors['max_site_deliverability_kw']}
                                  onSubmit={updateEquipmentUsage}
                                  fill="horizontal"
                                />
                              </Box>
                            </Box>
                          )}
                        </Box>
                      ) : (
                        <Box gap="xsmall">
                          <Box fill="horizontal" gap="xsmall">
                            <Text color="accent-1" size="small" toUpperCase>
                              {t('common:totalKwh')}
                            </Text>
                            <Text fontFamily="Lato, sans-serif">
                              {equipmentUsage.total_kwh ? formatNumber(equipmentUsage.total_kwh, true) : ''}
                            </Text>
                          </Box>
                          {showFciFields && (
                            <Box>
                              <Box fill="horizontal" gap="xsmall">
                                <Text color="accent-1" size="small" toUpperCase>
                                  {t('equipment:percentUptime')}
                                </Text>
                                <Text fontFamily="Lato, sans-serif">
                                  {equipmentUsage.percent_uptime
                                    ? formatNumber(equipmentUsage.percent_uptime, true)
                                    : ''}
                                </Text>
                              </Box>
                              <Box fill="horizontal" gap="xsmall">
                                <Text color="accent-1" size="small" toUpperCase>
                                  {t('equipment:maxSiteDeliverabilityKw')}
                                </Text>
                                <Text fontFamily="Lato, sans-serif">
                                  {equipmentUsage.max_site_deliverability_kw
                                    ? formatNumber(equipmentUsage.max_site_deliverability_kw, true)
                                    : ''}
                                </Text>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      )}
                      <Box fill="horizontal" gap="xsmall">
                        <Text color="accent-1" size="small" toUpperCase>
                          {t('equipment:recsRequired')}
                        </Text>
                        <Text fontFamily="Lato, sans-serif">
                          {formatNumber(equipmentUsage.recs_required, true) || '0.00'}
                        </Text>
                      </Box>
                      <Box fill="horizontal" gap="xsmall">
                        <Text color="accent-1" size="small" toUpperCase>
                          {t('equipment:creditsGenerated')}
                        </Text>
                        <Text fontFamily="Lato, sans-serif">
                          {formatNumber(equipmentUsage.credits_generated, true) || '0.00'}
                        </Text>
                      </Box>
                    </Box>
                  </FormCardSection>
                )}
                {!isMetered && (
                  <FormCardSection title={t('equipment:usageDetails')}>
                    {canEditQuarter && showFciFields ? (
                      <Box direction="row" gap="1rem">
                        <Input
                          label={t('equipment:percentUptime')}
                          componentType="percentage"
                          value={formValues['percent_uptime'] ? (formValues['percent_uptime'] as string) : ''}
                          setValue={(value) => updateFormValue('percent_uptime', value ? parseInt(value) : null)}
                          name="percent_uptime"
                          error={formErrors['percent_uptime']}
                          onSubmit={updateEquipmentUsage}
                          required
                          fill="horizontal"
                        />
                        <Input
                          label={t('equipment:maxSiteDeliverabilityKw')}
                          value={
                            formValues['max_site_deliverability_kw']
                              ? (formValues['max_site_deliverability_kw'] as string)
                              : ''
                          }
                          setValue={(value) =>
                            updateFormValue('max_site_deliverability_kw', value ? parseInt(value) : null)
                          }
                          name="max_site_deliverability_kw"
                          error={formErrors['max_site_deliverability_kw']}
                          onSubmit={updateEquipmentUsage}
                          fill="horizontal"
                        />
                      </Box>
                    ) : !canEditQuarter && showFciFields ? (
                      <Box direction="row" gap="1rem">
                        <Box fill="horizontal" gap="xsmall">
                          <Text color="accent-1" size="small" toUpperCase>
                            {t('equipment:percentUptime')}
                          </Text>
                          <Text fontFamily="Lato, sans-serif">
                            {formatNumber(equipmentUsage.percent_uptime ?? '', true) || '-'}
                          </Text>
                        </Box>
                        <Box fill="horizontal" gap="xsmall">
                          <Text color="accent-1" size="small" toUpperCase>
                            {t('equipment:maxSiteDeliverabilityKw')}
                          </Text>
                          <Text fontFamily="Lato, sans-serif">
                            {equipmentUsage.max_site_deliverability_kw
                              ? formatNumber(equipmentUsage.max_site_deliverability_kw, true)
                              : ''}
                          </Text>
                        </Box>
                      </Box>
                    ) : (
                      []
                    )}

                    {canEditQuarter ? (
                      <Box direction="row" gap="1rem">
                        <Input
                          label={t('equipment:chargeCyclesShift')}
                          value={formValues['charge_cycles_per_shift'] as string}
                          setValue={(value) => updateFormValue('charge_cycles_per_shift', value)}
                          name="charge_cycles_per_shift"
                          error={formErrors['charge_cycles_per_shift']}
                          onSubmit={updateEquipmentUsage}
                          required
                          fill="horizontal"
                        />
                        <Input
                          label={t('equipment:shiftsDay')}
                          value={formValues['shifts_per_day'] as string}
                          setValue={(value) => updateFormValue('shifts_per_day', value)}
                          name="shifts_per_day"
                          error={formErrors['shifts_per_day']}
                          onSubmit={updateEquipmentUsage}
                          required
                          fill="horizontal"
                        />
                        <Input
                          label={t('equipment:workDaysQuarter')}
                          value={formValues['work_days_per_quarter'] as string}
                          setValue={(value) => updateFormValue('work_days_per_quarter', value)}
                          name="work_days_per_quarter"
                          error={formErrors['work_days_per_quarter']}
                          onSubmit={updateEquipmentUsage}
                          required
                          fill="horizontal"
                        />
                      </Box>
                    ) : (
                      <Box direction="row" gap="1rem">
                        <Box fill="horizontal" gap="xsmall">
                          <Text color="accent-1" size="small" toUpperCase>
                            {t('equipment:chargeCyclesShift')}
                          </Text>
                          <Text fontFamily="Lato, sans-serif">
                            {formatNumber(equipmentUsage.charge_cycles_per_shift ?? '', true) || '-'}
                          </Text>
                        </Box>
                        <Box fill="horizontal" gap="xsmall">
                          <Text color="accent-1" size="small" toUpperCase>
                            {t('equipment:shiftsDay')}
                          </Text>
                          <Text fontFamily="Lato, sans-serif">
                            {formatNumber(equipmentUsage.shifts_per_day ?? '', true) || '-'}
                          </Text>
                        </Box>
                        <Box fill="horizontal" gap="xsmall">
                          <Text color="accent-1" size="small" toUpperCase>
                            {t('equipment:workDaysQuarter')}
                          </Text>
                          <Text fontFamily="Lato, sans-serif">
                            {formatNumber(equipmentUsage.work_days_per_quarter ?? '', true) || '-'}
                          </Text>
                        </Box>
                      </Box>
                    )}
                    {canEditQuarter ? (
                      <Box direction="row" gap="1rem">
                        <Input
                          label={t('equipment:chargerEfficiencyRating')}
                          value={formValues['percent_charger_efficiency_rating'] + (!canEditQuarter ? ' %' : '')}
                          setValue={(value) => updateFormValue('percent_charger_efficiency_rating', value)}
                          name="percent_charger_efficiency_rating"
                          componentType={canEditQuarter ? 'percentage' : undefined}
                          error={formErrors['percent_charger_efficiency_rating']}
                          onSubmit={updateEquipmentUsage}
                          disabled={!canEditQuarter}
                          disabledBackground="white"
                          required
                          fill="horizontal"
                        />
                        <Input
                          label={t('equipment:chargeReturnFactor')}
                          value={formValues['percent_charge_return_factor'] + (!canEditQuarter ? ' %' : '')}
                          setValue={(value) => updateFormValue('percent_charge_return_factor', value)}
                          name="percent_charge_return_factor"
                          componentType={canEditQuarter ? 'percentage' : undefined}
                          error={formErrors['percent_charge_return_factor']}
                          onSubmit={updateEquipmentUsage}
                          disabled={!canEditQuarter}
                          disabledBackground="white"
                          required
                          fill="horizontal"
                        />
                        <Input
                          label={t('equipment:depthOfDischarge')}
                          value={formValues['percent_depth_of_discharge'] + (!canEditQuarter ? ' %' : '')}
                          setValue={(value) => updateFormValue('percent_depth_of_discharge', value)}
                          name="percent_depth_of_discharge"
                          componentType={canEditQuarter ? 'percentage' : undefined}
                          error={formErrors['percent_depth_of_discharge']}
                          onSubmit={updateEquipmentUsage}
                          disabled={!canEditQuarter}
                          disabledBackground="white"
                          required
                          fill="horizontal"
                        />
                      </Box>
                    ) : (
                      <Box direction="row" gap="1rem">
                        <Box fill="horizontal" gap="xsmall">
                          <Text color="accent-1" size="small" toUpperCase>
                            {t('equipment:chargerEfficiencyRating')}
                          </Text>
                          <Text fontFamily="Lato, sans-serif">
                            {equipmentUsage.percent_charger_efficiency_rating
                              ? `${formatNumber(equipmentUsage.percent_charger_efficiency_rating, true)} %`
                              : '-'}
                          </Text>
                        </Box>
                        <Box fill="horizontal" gap="xsmall">
                          <Text color="accent-1" size="small" toUpperCase>
                            {t('equipment:chargeReturnFactor')}
                          </Text>
                          <Text fontFamily="Lato, sans-serif">
                            {equipmentUsage.percent_charge_return_factor
                              ? `${formatNumber(equipmentUsage.percent_charge_return_factor, true)} %`
                              : '-'}
                          </Text>
                        </Box>
                        <Box fill="horizontal" gap="xsmall">
                          <Text color="accent-1" size="small" toUpperCase>
                            {t('equipment:depthOfDischarge')}
                          </Text>
                          <Text fontFamily="Lato, sans-serif">
                            {equipmentUsage.percent_depth_of_discharge
                              ? `${formatNumber(equipmentUsage.percent_depth_of_discharge, true)} %`
                              : '-'}
                          </Text>
                        </Box>
                      </Box>
                    )}
                    <Line margin="0.5rem" />
                    <Box direction="row" gap="1rem">
                      <Box fill="horizontal" gap="xsmall">
                        <Text color="accent-1" size="small" toUpperCase>
                          {t('common:totalKwh')}
                        </Text>
                        <Text fontFamily="Lato, sans-serif">
                          {equipmentUsage.total_kwh ? formatNumber(equipmentUsage.total_kwh, true) : ''}
                        </Text>
                      </Box>
                      <Box fill="horizontal" gap="xsmall">
                        <Text color="accent-1" size="small" toUpperCase>
                          {t('equipment:kwhPerChargeCycle')}
                        </Text>
                        <Text fontFamily="Lato, sans-serif">
                          {formatNumber(equipmentUsage.kwh_per_charge_cycle?.toFixed(2) ?? '', true) || '0.00'}
                        </Text>
                      </Box>
                      <Box fill="horizontal" gap="xsmall">
                        <Text color="accent-1" size="small" toUpperCase>
                          {t('equipment:creditsGenerated')}
                        </Text>
                        <Text fontFamily="Lato, sans-serif">
                          {formatNumber(equipmentUsage.credits_generated, true) || '0.00'}
                        </Text>
                      </Box>
                    </Box>
                  </FormCardSection>
                )}
              </Box>
            </Box>
          )}
        </FormCard>
      </Form>
    </FormPage>
  );
});

export type EquipmentUsageDetailsProps = object;
